export const privacyPolicyMeta = {
  title: "Data Protection Policy",
  subtitle: "GDPR & Kenya Data Protection Act, 2019",
  version: "1.0.0",
  firstVersionDate: "3 August 2026",
  designation: "Data Analysts / Scientists / Engineers",
  processExecutor: "Christine Ojuok",
  processChecker: "",
  policyDate: "15 September 2026",
  approvals: [
    { role: "Drafter", name: "Dorris Lumumba", signature: "", date: "" },
    { role: "Head of Department", name: "Christine Ojuok", signature: "", date: "17/08/2026" },
  ],
};

export const privacyPolicySections = [
  {
    id: "policy-statement",
    number: "1",
    title: "Policy Statement",
    blocks: [
      {
        type: "p",
        text: 'InfoTrace Analytics Limited ("the Company") collects and processes personal data to carry out its everyday business functions, including the products and services defined by its business type. Such data is collected from employees, customers, suppliers and clients and includes, but is not limited to, names, addresses, email addresses, IP addresses, identification numbers, and other private, confidential or sensitive information.',
      },
      {
        type: "p",
        text: "The Company operates across multiple jurisdictions, including Kenya and the European Economic Area, and processes personal data both as a data controller and as a data processor. The Company is therefore committed to processing all personal data in accordance with:",
      },
      {
        type: "ul",
        items: [
          'the General Data Protection Regulation (EU) 2016/679 ("GDPR"), where it processes personal data of individuals in the EU/EEA or otherwise falls within the territorial scope of the GDPR;',
          'the Kenya Data Protection Act, No. 24 of 2019 ("Kenya DPA"), the Data Protection (General) Regulations, 2021, the Data Protection (Registration of Data Controllers and Data Processors) Regulations, 2021, and the Data Protection (Compliance and Enforcement) Regulations, 2021, where it processes personal data of individuals in Kenya or is established or operating in Kenya; and',
          "any other data protection law applicable in a jurisdiction in which the Company operates or in which its data subjects are located,",
        ],
      },
      {
        type: "p",
        text: '(collectively, "the data protection laws"). Where requirements differ between applicable regimes, the Company applies the higher standard of protection to the data subject.',
      },
      {
        type: "p",
        text: "The Company has developed policies, procedures, controls and measures to ensure continued compliance with the data protection laws, including staff training, procedure documents, audit measures and assessments, and operates a 'Privacy by Design' approach, assessing changes and their impact from the outset.",
      },
    ],
  },
  {
    id: "purpose",
    number: "2",
    title: "Purpose",
    blocks: [
      {
        type: "p",
        text: "The purpose of this policy is to ensure that the Company meets its legal, statutory and regulatory requirements under the data protection laws — including registration, governance, breach notification and cross-border transfer obligations under the Kenya DPA, alongside its GDPR obligations — and that all personal and sensitive/special category data is processed compliantly and in the individual's best interest.",
      },
      {
        type: "p",
        text: "This policy also serves as a reference document for employees and third parties on their responsibilities when handling personal data and data subject requests, whichever legal regime applies to the processing in question.",
      },
    ],
  },
  {
    id: "scope",
    number: "3",
    title: "Scope",
    blocks: [
      {
        type: "p",
        text: "This policy applies to all staff within the Company (meaning permanent, fixed-term and temporary staff, third-party representatives or sub-contractors, agency workers, volunteers, interns and agents engaged with the Company), and to all processing of personal information carried out by or on behalf of the Company, in Kenya, the EU/EEA, or any other jurisdiction in which it operates. Adherence to this policy is mandatory and non-compliance may lead to disciplinary action.",
      },
    ],
  },
  {
    id: "legal-framework",
    number: "4",
    title: "Legal & Regulatory Framework — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "The Company's data protection obligations in Kenya derive from:",
      },
      {
        type: "ul",
        items: [
          "Article 31 of the Constitution of Kenya, 2010 (right to privacy);",
          "the Data Protection Act, No. 24 of 2019, which came into force on 25 November 2019 and established the Office of the Data Protection Commissioner (ODPC) as the independent supervisory authority for Kenya;",
          "the Data Protection (General) Regulations, 2021;",
          "the Data Protection (Registration of Data Controllers and Data Processors) Regulations, 2021; and",
          "the Data Protection (Compliance and Enforcement) Regulations, 2021.",
        ],
      },
      {
        type: "p",
        text: "These sit alongside, and do not replace, the Company's obligations under the GDPR and any equivalent law in force in another jurisdiction where it processes personal data. Employees processing personal data of individuals located in Kenya, or on behalf of Kenyan clients, must apply the Kenya-specific requirements set out in Sections 8, 10, 13, 14 and 15 of this policy in addition to the general principles applicable throughout this document.",
      },
    ],
  },
  {
    id: "definitions",
    number: "5",
    title: "Definitions",
    blocks: [
      {
        type: "definitions",
        items: [
          {
            term: "Biometric data",
            text: "personal data resulting from specific technical processing relating to the physical, physiological or behavioural characteristics of a natural person which allow or confirm their unique identification, such as facial images or fingerprint data.",
          },
          {
            term: "Cabinet Secretary",
            text: "the Cabinet Secretary responsible for matters relating to data protection in Kenya, who may prescribe adequacy findings and data-localisation requirements under the Kenya DPA.",
          },
          {
            term: "Consent",
            text: "any freely given, specific, informed and unambiguous indication of the data subject's wishes, given by statement or clear affirmative action; under the Kenya DPA, consent must additionally be express, unequivocal and evidenced, with the burden of proving valid consent resting on the Company.",
          },
          {
            term: "Cross-Border Processing / Transfer",
            text: "under the GDPR, processing taking place in, or substantially affecting data subjects in, more than one EU Member State; under the Kenya DPA, the movement of personal data outside Kenya, whether by formal transfer, remote/offshore access, disaster-recovery replication, or processing on a foreign-hosted platform.",
          },
          {
            term: "Data Commissioner / ODPC",
            text: "the Data Commissioner and the Office of the Data Protection Commissioner, the independent body established under the Kenya DPA responsible for registration, enforcement, guidance and oversight of data protection compliance in Kenya.",
          },
          {
            term: "Data controller",
            text: "the natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of processing personal data.",
          },
          {
            term: "Data processor",
            text: "a natural or legal person, public authority, agency or other body which processes personal data on behalf of a controller.",
          },
          {
            term: "Data protection laws",
            text: "for the purposes of this document, the collective description of the GDPR, the Kenya DPA and its subsidiary regulations, and any other relevant data protection law the Company complies with.",
          },
          {
            term: "Data subject",
            text: "an individual who is the subject of personal data.",
          },
          {
            term: "GDPR",
            text: "the General Data Protection Regulation (EU) 2016/679.",
          },
          {
            term: "Genetic data",
            text: "personal data relating to the inherited or acquired genetic characteristics of a natural person which give unique information about their physiology or health.",
          },
          {
            term: "Kenya DPA",
            text: "the Data Protection Act, No. 24 of 2019 (Laws of Kenya), and its subsidiary regulations referenced in Section 4 above.",
          },
          {
            term: "Personal data",
            text: "any information relating to an identified or identifiable natural person, including a name, identification number, location data, an online identifier, or one or more factors specific to that person's physical, physiological, genetic, mental, economic, cultural or social identity.",
          },
          {
            term: "Processing",
            text: "any operation performed on personal data, whether or not by automated means, including collection, recording, storage, use, disclosure, alignment, restriction, erasure or destruction.",
          },
          {
            term: "Profiling",
            text: "any form of automated processing of personal data used to evaluate personal aspects of a natural person, such as economic situation, health, preferences, reliability, behaviour, location or movements.",
          },
          {
            term: "Recipient",
            text: "a natural or legal person, public authority, agency or other body to which personal data is disclosed, whether a third party or not.",
          },
          {
            term: "Register",
            text: "the register of data controllers and data processors maintained by the Data Commissioner under the Kenya DPA.",
          },
          {
            term: "Sensitive Personal Data (Kenya DPA) / Special Category Data (GDPR)",
            text: "data revealing a natural person's race, health status, ethnic or social origin, conscience, belief, genetic data, biometric data, property details, marital status, family details (including the names of a person's children, parents, spouse or spouses), sex, sexual orientation, or criminal history. This is broader than the GDPR's 'special categories' (which does not, for example, extend to property details, marital status or family details), and both definitions apply concurrently where relevant.",
          },
          {
            term: "Supervisory Authority",
            text: "an independent public authority responsible for enforcing data protection law; in the EU/EEA this is the relevant Member State authority, and in Kenya this is the Data Commissioner / ODPC.",
          },
          {
            term: "Third Party",
            text: "a natural or legal person, public authority, agency or body other than the data subject, under the Company's direct authority, or authorised to process personal data on the Company's behalf.",
          },
        ],
      },
    ],
  },
  {
    id: "core-principles",
    number: "6",
    title: "Core Data Protection Principles",
    blocks: [
      {
        type: "p",
        text: "Article 5 of the GDPR, mirrored closely by Section 25 of the Kenya DPA, requires that personal data be:",
      },
      {
        type: "ul",
        items: [
          "processed lawfully, fairly and in a transparent manner ('lawfulness, fairness and transparency');",
          "collected for specified, explicit and legitimate purposes and not further processed incompatibly with those purposes ('purpose limitation');",
          "adequate, relevant and limited to what is necessary ('data minimisation');",
          "accurate and, where necessary, kept up to date ('accuracy');",
          "kept in identifiable form for no longer than necessary ('storage limitation'); and",
          "processed with appropriate security, including protection against unauthorised or unlawful processing and accidental loss, destruction or damage ('integrity and confidentiality').",
        ],
      },
      {
        type: "p",
        text: "The Kenya DPA additionally requires, under Section 25, that personal data be processed in accordance with the data subject's right to privacy, and that where sensitive personal data is involved, additional safeguards under Sections 44–45 apply (see Section 13 below).",
      },
    ],
  },
  {
    id: "objectives",
    number: "7",
    title: "Objectives",
    blocks: [
      {
        type: "p",
        text: "The Company is committed to ensuring that all personal data it processes is handled in accordance with the data protection laws and their principles, together with any associated regulations or codes of conduct laid down by the relevant Supervisory Authority (the ODPC in Kenya, and the competent authority in any EU Member State) and local law. The Company ensures that:",
      },
      {
        type: "ul",
        items: [
          "it protects the rights of individuals with regard to the processing of personal information;",
          "it develops, implements and maintains a data protection policy, procedure, audit plan and training programme for compliance with the data protection laws;",
          "every business practice, function and process is monitored for compliance with the data protection laws and their principles;",
          "data is only processed where the lawfulness-of-processing requirements have been met;",
          "sensitive personal data / special category data is only processed in accordance with the GDPR and, where Kenyan data subjects are involved, the conditions in Sections 44 and 45 of the Kenya DPA and the Data Protection (General) Regulations, 2021;",
          "consent is recorded at the time it is obtained and can be evidenced to the relevant Supervisory Authority on request;",
          "all employees (including new starters and agents) are trained on their obligations under the data protection laws as they apply to their role;",
          "a continuous programme of monitoring, review and improvement is maintained to identify gaps and non-compliance before they become a risk;",
          "robust and documented complaint-handling and data-breach controls are in place, meeting both the GDPR's and the Kenya DPA's notification timelines (see Section 14);",
          "the Company registers as a data controller and/or data processor with the ODPC where required (see Section 8), and has appointed a Data Protection Officer under Article 37 of the GDPR whose remit also covers the Company's Kenya DPA obligations (see Section 9);",
          "a dedicated Audit & Monitoring Programme is in place, reviewed against the Company's data protection policies, procedures and the relevant regulations; and",
          "personal information is stored and destroyed in accordance with the data protection laws' timeframes and requirements, and information is provided to individuals in a concise, transparent, intelligible and easily accessible form.",
        ],
      },
    ],
  },
  {
    id: "odpc-registration",
    number: "8",
    title: "Registration with the Office of the Data Protection Commissioner (ODPC) — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "Section 18 of the Kenya DPA provides that no person may act as a data controller or data processor in respect of personal data of individuals in Kenya unless registered with the Data Commissioner. The Data Protection (Registration of Data Controllers and Data Processors) Regulations, 2021 set out the applicable thresholds. Registration is mandatory for an entity that:",
      },
      {
        type: "ul",
        items: [
          "has an annual turnover or revenue of at least KES 5,000,000; or",
          "employs more than 10 people; or",
          "processes sensitive personal data, regardless of turnover or employee count; or",
          "falls within a sector designated by the ODPC as requiring registration regardless of size (for example, financial services, health, or telecommunications).",
        ],
      },
      {
        type: "p",
        text: "Applications are submitted electronically through the ODPC's online platform. On approval, a certificate of registration is issued, valid for 24 months and renewable thereafter. Where the Company meets the applicable thresholds for its Kenya operations or client engagements, the Head of Compliance / Data Protection Officer is responsible for ensuring registration is completed, kept current, and renewed before expiry, and for maintaining evidence of registration for audit purposes.",
      },
    ],
  },
  {
    id: "dpo",
    number: "9",
    title: "Data Protection Officer",
    blocks: [
      {
        type: "p",
        text: "The Company has appointed a Data Protection Officer (DPO) whose role is to identify and mitigate risks to the protection of personal data, act in an advisory capacity to the business, its employees and senior management, and stay informed of legislative developments in data protection. The DPO works with the IT Manager and IT Team to ensure that all processes, systems and staff operate compliantly with the data protection laws.",
      },
      {
        type: "p",
        text: "Under the GDPR, the DPO performs the duties set out under Article 37. Under the Kenya DPA, a DPO must additionally be designated where the Company (or a client on whose behalf it processes data):",
      },
      {
        type: "ul",
        items: [
          "is a public body;",
          "processes sensitive personal data on a large scale; or",
          "carries out core activities requiring regular and systematic monitoring of data subjects on a large scale.",
        ],
      },
      {
        type: "p",
        text: "Where any of these Kenya-specific triggers apply, the Company designates the same DPO to cover both regimes, and files the DPO's particulars with the ODPC as part of, or alongside, its registration under Section 8. The DPO has overall responsibility for due diligence, privacy impact assessments, risk analysis and data transfers involving personal data, and maintains records and management reports in line with the data protection laws and the Company's internal objectives.",
      },
    ],
  },
  {
    id: "privacy-by-design",
    number: "10",
    title: "Privacy by Design",
    blocks: [
      {
        type: "p",
        text: "The Company operates a 'Privacy by Design' approach and ethos, aiming to mitigate the risks associated with processing personal data through prevention, embedded in its processes, systems and activities. This satisfies both Article 25 GDPR and the equivalent expectation under Section 25 of the Kenya DPA. Additional measures include:",
      },
      { type: "h4", text: "Data Minimisation" },
      {
        type: "p",
        text: "Under Article 5(c) GDPR and the equivalent Kenya DPA principle, data must be 'limited to what is necessary'. The Company only obtains, retains, processes and shares the data essential to carry out its services and legal obligations, and only keeps it for as long as necessary. Measures include:",
      },
      {
        type: "ul",
        items: [
          "electronic collection forms only include fields relevant to the purpose of collection; optional fields are not used, as 'optional' implies the data is not necessary;",
          "physical collection (face-to-face, telephone) is supported by scripts and internal forms limited to predefined, necessary fields;",
          "SLAs and bespoke agreements with third-party controllers who send the Company personal information specify that only relevant and necessary data is to be provided; and",
          "documented destruction procedures apply where a data subject or third party provides personal information surplus to requirement.",
        ],
      },
      { type: "h4", text: "Pseudonymisation" },
      {
        type: "p",
        text: "The Company utilises pseudonymisation where possible, so that data cannot be attributed to a specific data subject without separate additional information (personal identifiers). Encryption and partitioning are used to keep personal identifiers separate from pseudonymised data sets, and the Company recognises that pseudonymised data subjects may still be indirectly identifiable, so this technique is used alongside other technical and operational risk-reduction measures.",
      },
      { type: "h4", text: "Encryption" },
      {
        type: "p",
        text: "The Company treats encryption as a form of pseudonymisation and also as a secondary risk-prevention measure for securing personal data held. Encryption with a secret key is used to render data indecipherable without the assigned key, and is used when transferring personal data to any external party, with the key provided in a separate format. Where sensitive personal data / special category data is being transferred or disclosed, the DPO must authorise the transfer and review the encryption method for compliance and accuracy.",
      },
      { type: "h4", text: "Restriction" },
      {
        type: "p",
        text: "The Company uses company-wide restriction methods for all personal data activities. Restricting access is built into the foundation of the Company's processes, systems and structure, ensuring that only those with authorisation and a relevant purpose have access to personal information.",
      },
      { type: "h4", text: "Hard Copy Data" },
      {
        type: "p",
        text: "Where it is essential to obtain, process or share personal or sensitive/special category information only available in paper format, the Company uses a tiered approach to minimise the information it holds and the length of time it is held:",
      },
      {
        type: "ul",
        items: [
          "in the first instance, the Company asks the initial data controller to send copies of any personal information records directly to the data subject;",
          "where that is not feasible, the Company obtains a copy and redacts it, where applicable, so only relevant information remains;",
          "where only mandatory information is visible, electronic formats are used to send information to the recipient so encryption can be applied (the postal system is not used, as it can be intercepted);",
          "recipients are re-verified before transfer;",
          "the DPO authorises the transfer and checks the file(s), encryption method and key; and",
          "once receipt is confirmed, and where permitted by the data protection laws, hard copy data is destroyed and the sent message deleted; if a copy must be retained, it is stored in a physical safe rather than the standard archiving system.",
        ],
      },
    ],
  },
  {
    id: "information-audit",
    number: "11",
    title: "Information Audit and Records of Processing",
    blocks: [
      {
        type: "p",
        text: "The Company has carried out a company-wide data protection information audit to record, categorise and protect the personal data it holds and processes as controller and/or processor. This is compiled on a central register recording: what personal data is held; where it came from; who it is shared with; the legal basis for processing; its format(s); who is responsible for it; and disclosures and transfers. This register also underpins the particulars the Company must supply to the ODPC on registration (Section 8) and satisfies the GDPR Article 30 records-of-processing obligation.",
      },
    ],
  },
  {
    id: "legal-basis",
    number: "12",
    title: "Legal Basis for Processing",
    blocks: [
      {
        type: "p",
        text: "Before carrying out any processing activity, the Company identifies and establishes the legal basis for doing so. This mirrors both Article 6 GDPR and Section 30 of the Kenya DPA. Data is only obtained, processed or stored where:",
      },
      {
        type: "ul",
        items: [
          "the data subject has given consent for one or more specific purposes;",
          "processing is necessary for the performance of a contract with the data subject, or to take steps at their request before entering into a contract;",
          "processing is necessary for compliance with a legal obligation;",
          "processing is necessary to protect the vital interests of the data subject or another natural person;",
          "processing is necessary for a task carried out in the public interest or in the exercise of official authority vested in the Company; or",
          "processing is necessary for the Company's or a third party's legitimate interests, except where overridden by the data subject's interests or fundamental rights, particularly where the data subject is a child.",
        ],
      },
      {
        type: "p",
        text: "Under Section 32 of the Kenya DPA, where consent is relied upon, it must be express, unequivocal, freely given and evidenced; the Company bears the burden of proving valid consent, and data subjects may withdraw consent at any time without affecting the lawfulness of processing carried out before withdrawal.",
      },
    ],
  },
  {
    id: "sensitive-data",
    number: "13",
    title: "Sensitive Personal Data / Special Category Data — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "Special categories of personal data are defined under the GDPR as data revealing racial or ethnic origin, political opinions, religious or philosophical beliefs, trade union membership, genetic data, biometric data used for identification, health data, or data concerning a person's sex life or sexual orientation. Processing of such data is prohibited unless an Article 9 GDPR condition applies.",
      },
      {
        type: "p",
        text: "The Kenya DPA's definition of 'sensitive personal data' is broader: it additionally covers property details, marital status, family details (including the names of a person's children, parents, spouse or spouses), and criminal history, alongside race, health status, ethnic/social origin, conscience, belief, genetic data, biometric data, sex and sexual orientation. Where the Company processes data of Kenyan data subjects, it applies the wider Kenya DPA definition.",
      },
      {
        type: "p",
        text: "The Company only processes sensitive personal data / special category data where one of the following applies, and records which basis is relied upon:",
      },
      {
        type: "ul",
        items: [
          "the data subject has given explicit consent;",
          "processing is necessary for compliance with a legal or regulatory obligation (for example, know-your-customer or anti-money-laundering checks in financial services);",
          "processing is necessary to protect the vital interests of the data subject or another person who is physically or legally incapable of giving consent;",
          "processing is carried out by, or under the responsibility of, a health professional, for health-related purposes and subject to confidentiality obligations;",
          "processing relates to data manifestly made public by the data subject;",
          "processing is necessary for the establishment, exercise or defence of legal claims; or",
          "processing is necessary for reasons of substantial public interest, on a basis proportionate to the aim pursued and with appropriate safeguards.",
        ],
      },
      {
        type: "p",
        text: "Where sensitive personal data / special category data is processed, additional safeguards apply, including DPO authorisation of any transfer (Section 10), and, where the processing is high-risk, a Data Protection Impact Assessment (Section 15).",
      },
    ],
  },
  {
    id: "childrens-data",
    number: "14",
    title: "Children's Personal Data — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "Section 33 of the Kenya DPA provides that a data controller or data processor shall not process personal data relating to a child unless consent is given by the child's parent or guardian, and the processing is carried out in a manner that protects and advances the rights and best interests of the child. Consistent with this, and with the GDPR's equivalent protections for children's data, the Company:",
      },
      {
        type: "ul",
        items: [
          "puts in place mechanisms to verify, so far as reasonably practicable, whether a data subject is a minor before relying on the data subject's own consent;",
          "obtains consent from a parent or guardian before processing a child's personal data, where the Company's own processes involve minors;",
          "does not require children to provide more personal data than is necessary to access a product or service; and",
          "applies enhanced data-minimisation and security measures to any personal data relating to children.",
        ],
      },
    ],
  },
  {
    id: "cross-border",
    number: "15",
    title: "Cross-Border Data Transfers — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "Sections 48 to 50 of the Kenya DPA govern the transfer of personal data outside Kenya, which occurs functionally, not just formally — remote offshore access, disaster-recovery replication and processing via a foreign-hosted platform all trigger the regime, in the same way a formal outsourcing arrangement would. A transfer of Kenyan personal data outside Kenya is only lawful where one of the following applies:",
      },
      {
        type: "ul",
        items: [
          "Adequacy: the Cabinet Secretary has confirmed the receiving country or organisation provides an adequate level of data protection. As at the date of this policy, no adequacy findings have been issued in respect of any country or sector, so this ground is not currently available in practice;",
          "Appropriate safeguards: the Company has put in place appropriate safeguards, such as standard contractual clauses, binding corporate rules for intra-group transfers, or another approved mechanism, and can demonstrate to the Data Commissioner that data subjects have enforceable rights and effective remedies;",
          "Explicit consent: the data subject has given explicit, informed consent to the specific transfer; or",
          "Necessity-based derogations: the transfer is necessary for the performance of a contract, for reasons of substantial public interest, for the establishment or defence of legal claims, or to protect the vital interests of the data subject, in each case within the narrow terms permitted by the Act.",
        ],
      },
      {
        type: "p",
        text: "Section 50 imposes heightened conditions on transfers of sensitive personal data or data relating to certain strategic national interests, and preserves the Cabinet Secretary's power to require that specified categories of data be processed on a server or data centre located in Kenya (data localisation). Where the Company engages an overseas cloud, hosting, analytics or business-intelligence provider in respect of Kenyan client data — including financial services data — the DPO must confirm the applicable transfer ground, document it in a data transfer agreement, and assess whether any data-localisation designation applies before the transfer proceeds.",
      },
      {
        type: "p",
        text: "For transfers of personal data originating in the EU/EEA, the Company continues to rely on the GDPR's Chapter V mechanisms (adequacy decisions, Standard Contractual Clauses, or Binding Corporate Rules), applied independently of the Kenya DPA ground used for any Kenyan leg of the same data flow.",
      },
    ],
  },
  {
    id: "data-subject-rights",
    number: "16",
    title: "Data Subject / Individual Rights",
    blocks: [
      {
        type: "p",
        text: "Individuals whose personal data the Company processes have rights under both the GDPR and, where applicable, Section 26 of the Kenya DPA. These substantially overlap, and the Company handles requests under a single process regardless of which regime applies, giving effect to the right to:",
      },
      {
        type: "ul",
        items: [
          "be informed of the use to which their personal data will be put;",
          "access their personal data held by the Company as controller or processor;",
          "object to the processing of all or part of their personal data;",
          "correction or rectification of inaccurate or misleading data;",
          "erasure of false or misleading data, or data processed unlawfully;",
          "restriction of processing in defined circumstances; and",
          "data portability and not to be subject to a decision based solely on automated processing, including profiling, which produces legal or similarly significant effects, without human review.",
        ],
      },
      {
        type: "p",
        text: "Under the Kenya DPA, a right may be exercised on a data subject's behalf: by a parent or person with parental authority, or a guardian, where the data subject is a minor; by a duly authorised guardian or administrator, where the data subject has a mental or other disability; or by any other person duly authorised by the data subject.",
      },
    ],
  },
  {
    id: "retention",
    number: "17",
    title: "Data Retention and Disposal",
    blocks: [
      {
        type: "p",
        text: "The Company has defined procedures for adhering to the retention periods set out by the relevant laws, contracts and business requirements, in line with the data protection laws' requirement to hold and process personal information only for as long as necessary. All personal data is disposed of in a way that protects the rights and privacy of data subjects (for example, shredding, disposal as confidential waste, or secure electronic deletion), prioritising the protection of personal data at all times.",
      },
    ],
  },
  {
    id: "breach-notification",
    number: "18",
    title: "Personal Data Breach Notification — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "The Company maintains breach detection, assessment and reporting procedures that meet both regimes' timelines:",
      },
      {
        type: "ul",
        items: [
          "under the GDPR, the Company notifies the competent supervisory authority without undue delay and, where feasible, within 72 hours of becoming aware of a personal data breach, and notifies affected data subjects without undue delay where the breach is likely to result in a high risk to their rights and freedoms;",
          "under Section 43 of the Kenya DPA, the Company notifies the Data Commissioner within 72 hours of becoming aware of a breach that is likely to result in a risk to data subjects' rights and freedoms, and provides an explanation for any delay beyond that window;",
          "where a data processor engaged by the Company discovers a breach, it must notify the Company (as controller) within 48 hours of discovery, to allow the Company to meet the 72-hour ODPC deadline; and",
          "where a breach is likely to result in high risk to individuals, the Company notifies affected data subjects without undue delay, in clear, plain language, including practical steps they can take to protect themselves.",
        ],
      },
      {
        type: "p",
        text: "Breach notifications include a description of the breach, the categories and approximate number of data subjects affected, likely consequences, and measures taken or proposed to mitigate it. The Company maintains an internal breach register recording all incidents, including those below the notification threshold, as evidence of compliance for audit purposes.",
      },
    ],
  },
  {
    id: "dpia",
    number: "19",
    title: "Data Protection Impact Assessments — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "Under Article 35 GDPR and Section 31 of the Kenya DPA, the Company carries out a Data Protection Impact Assessment (DPIA) before undertaking processing likely to result in a high risk to the rights and freedoms of data subjects. This includes, without limitation: large-scale processing of sensitive personal data / special category data; profiling or automated decision-making with legal or similarly significant effects; processing of children's personal data at scale; cross-border transfers of sensitive personal data; and large-scale systematic monitoring. DPIAs are reviewed and, where required, submitted to the DPO for sign-off, and to the relevant Supervisory Authority for prior consultation where a residual high risk cannot be mitigated.",
      },
    ],
  },
  {
    id: "audit-monitoring",
    number: "20",
    title: "Audit and Monitoring",
    blocks: [
      {
        type: "p",
        text: "This policy details the extensive controls, measures and methods used by the Company to protect personal data, uphold the rights of data subjects, mitigate risks, minimise breaches and comply with the data protection laws. The Company also carries out regular audits and compliance monitoring processes, detailed in its Compliance Monitoring & Audit Policy & Procedure, to ensure that the measures and controls in place remain adequate, effective and compliant. The DPO has overall responsibility for assessing, testing, reviewing and improving these processes, and reports improvement action plans to Senior Management. All reviews, audits and monitoring are recorded by the DPO, with copies provided to Senior Management and made available to the relevant Supervisory Authority on request. Internal data protection audits aim to:",
      },
      {
        type: "ul",
        items: [
          "ensure appropriate policies and procedures are in place;",
          "verify that those policies and procedures are being followed;",
          "test the adequacy and effectiveness of the measures and controls in place;",
          "detect breaches or potential breaches of compliance;",
          "identify risks and assess mitigating actions; and",
          "recommend solutions and action plans to Senior Management for continued compliance with the data protection laws.",
        ],
      },
    ],
  },
  {
    id: "enforcement",
    number: "21",
    title: "Enforcement and Penalties — Kenya DPA 2019",
    blocks: [
      {
        type: "p",
        text: "Non-compliance with the data protection laws exposes the Company to regulatory and, in some cases, criminal liability. Under the Kenya DPA, the ODPC may issue compliance notices, enforcement notices and penalty notices, escalating where earlier notices are not addressed. Administrative fines can reach up to KES 5,000,000 or 1% of annual turnover for data controllers, and up to KES 3,000,000 or 0.5% of annual turnover for data processors, and certain offences carry criminal sanctions. Under the GDPR, fines can reach up to €20,000,000 or 4% of annual global turnover, whichever is higher, for the most serious infringements. All staff should treat compliance with this policy as a business-critical requirement, not only for legal reasons but to protect the trust our clients and their customers place in the Company.",
      },
    ],
  },
  {
    id: "responsibilities",
    number: "22",
    title: "Responsibilities",
    blocks: [
      {
        type: "p",
        text: "The Company has appointed a Data Protection Officer whose role is to identify and mitigate risks to the protection of personal data, act in an advisory capacity to the business, its employees and senior management, liaise with the ODPC and, where applicable, the relevant EU supervisory authority, and stay informed of legislative developments. The DPO works with the IT Manager and IT Team to ensure that all processes, systems and staff operate compliantly with the data protection laws, and has overall responsibility for due diligence, privacy impact assessments, risk analysis, registration and renewal with the ODPC, and data transfers involving personal data.",
      },
    ],
  },
  {
    id: "review",
    number: "23",
    title: "Review of this Policy",
    blocks: [
      {
        type: "p",
        text: "This policy is reviewed at least annually, and additionally whenever there is a material change in the data protection laws (including new ODPC guidance notes or regulations), the Company's processing activities, or its cross-border data flows. The next scheduled review is due no later than 12 months from the date of this version.",
      },
    ],
  },
];
